'use client';

import { Box } from '@mui/material';

import { FilterDropdown, TabButton } from '@/components';
import { useT } from '@/hooks';
import { TimeFilter } from '@/types';
import { formatCategoryDisplay, formatTimePeriodDisplay } from '@/utils';

interface LeaderboardFiltersProps {
  categories: string[];
  timePeriods: string[];
  selectedTimeFilter: TimeFilter;
  setSelectedTimeFilter: (val: TimeFilter) => void;
  selectedCategory: string;
  setSelectedCategory: (val: string) => void;
}

export const LeaderboardFilters = ({
  categories = [],
  timePeriods = [],
  selectedTimeFilter,
  setSelectedTimeFilter,
  selectedCategory,
  setSelectedCategory,
}: LeaderboardFiltersProps) => {
  const { t } = useT();
  const displayTimePeriods = timePeriods?.length > 0 ? timePeriods : ['DAY'];

  const renderFilter = (filter: TimeFilter) => {
    const isActive = selectedTimeFilter === filter;
    const label = formatTimePeriodDisplay(filter, t);
    const handleClick = () => setSelectedTimeFilter(filter);

    return <TabButton key={filter} label={label} isActive={isActive} onClick={handleClick} />;
  };

  const renderFilterList = displayTimePeriods?.map((f) => renderFilter(f as TimeFilter));

  const displayCategories = categories?.length > 0 ? categories : ['OVERALL'];

  const timePeriodOptions = displayTimePeriods.map((f) => ({
    value: f,
    label: formatTimePeriodDisplay(f, t),
  }));

  const categoryOptions = displayCategories.map((cat) => ({
    value: cat,
    label: formatCategoryDisplay(cat, t),
  }));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'space-between', md: 'space-between' },
        mb: 2,
        flexWrap: 'wrap',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Mobile Dropdown */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <FilterDropdown
            value={selectedTimeFilter}
            options={timePeriodOptions}
            onChange={(val) => setSelectedTimeFilter(val as TimeFilter)}
            minWidth={110}
            height={38}
            fontSize="0.9375rem"
          />
        </Box>

        {/* Desktop Tabs */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, flexWrap: 'wrap' }}>
          {renderFilterList}
        </Box>
      </Box>

      {/* Category Dropdown */}
      <FilterDropdown
        value={selectedCategory}
        options={categoryOptions}
        onChange={setSelectedCategory}
        minWidth={140}
        height={38}
        fontSize={{ xs: '0.9375rem', md: '0.8125rem' }}
      />
    </Box>
  );
};
